import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {apiFetch} from '../../utils/apiBase';
import {emitProductAnalytics} from '../../utils/productAnalytics';
import {buildContextualPlansPath, createUpgradeIntent, persistUpgradeIntent} from './upgradeIntent';
import {getConversionPrompt} from './conversionPrompts';

const EMPTY = Object.freeze({
  billing: null,
  loading: false,
  error: '',
  isFree: false,
  isPaid: false,
  canManageBilling: false,
  creationRemaining: null,
  aiFollowupRemaining: null,
  copiedFollowupRemaining: null,
  participantLimit: null,
  organizerLimit: null,
  refreshBilling: async () => null,
  openPlans: () => null,
  updateBilling: () => {},
});

export const ConversionContext = createContext(EMPTY);

function normalizeBillingPayload(data) {
  if (!data?.billing) return null;
  return {...data.billing, commercial: data.commercial || null};
}

function remaining(limit, used) {
  const safeLimit = Number(limit);
  const safeUsed = Number(used);
  if (!Number.isFinite(safeLimit) || !Number.isFinite(safeUsed)) return null;
  return Math.max(0, safeLimit - safeUsed);
}

export function ConversionProvider({companyId, userId, children}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(Boolean(companyId));
  const [error, setError] = useState('');

  const refreshBilling = useCallback(async () => {
    if (!companyId) return null;
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch(
        `/api/billing/status?companyId=${encodeURIComponent(companyId)}`,
        {method: 'GET', credentials: 'include'},
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.ok === false) throw new Error(data?.error || 'billing_status_unavailable');
      const next = normalizeBillingPayload(data);
      setBilling(next);
      return next;
    } catch (requestError) {
      setError(requestError?.message || 'billing_status_unavailable');
      return null;
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (!companyId || !userId) {
      setBilling(null);
      setLoading(false);
      setError('');
      return;
    }
    refreshBilling();
  }, [companyId, refreshBilling, userId]);

  const commercial = billing?.commercial;
  const isFree = commercial?.plan === 'free' && commercial?.billingStatus === 'free';
  const isPaid = commercial?.paidThrough === true;
  const canManageBilling = billing?.isBillingAdmin === true;
  const creationRemaining = remaining(
    commercial?.limits?.freeLifetimeCreationLimit,
    commercial?.usage?.freeLifetimeMapCreations,
  );
  const aiFollowupRemaining = remaining(
    commercial?.limits?.freeAiFollowupLimit,
    commercial?.usage?.freeAiFollowupsUsed,
  );
  const copiedFollowupRemaining = remaining(
    commercial?.limits?.freeCopiedFollowupLimit,
    commercial?.usage?.freeCopiedFollowupsUsed,
  );

  const openPlans = useCallback(
    ({reason = 'free_status', returnPath, returnAction, surface} = {}) => {
      if (!companyId || !isFree) return null;
      const prompt = getConversionPrompt(reason);
      const safeCurrentPath = `${location.pathname}${location.search || ''}${location.hash || ''}`;
      const intent = createUpgradeIntent({
        companyId,
        reason,
        returnAction: returnAction || prompt.returnAction,
        returnPath: returnPath || safeCurrentPath,
      });
      if (!intent) return null;
      persistUpgradeIntent(intent);
      emitProductAnalytics('upgrade_prompt_clicked', {
        reason,
        surface: surface || prompt.analyticsSource,
        billingAuthority: canManageBilling,
        usageState:
          creationRemaining === 0 ? 'exhausted' : creationRemaining === 1 ? 'one_remaining' : 'unused',
        returnAction: intent.returnAction,
      });
      navigate(buildContextualPlansPath(companyId, reason));
      return intent;
    },
    [canManageBilling, companyId, creationRemaining, isFree, location, navigate],
  );

  const updateBilling = useCallback((next) => {
    if (next?.commercial) setBilling(next);
  }, []);

  const value = useMemo(
    () => ({
      billing,
      loading,
      error,
      isFree,
      isPaid,
      canManageBilling,
      creationRemaining,
      aiFollowupRemaining,
      copiedFollowupRemaining,
      participantLimit: Number.isFinite(Number(commercial?.limits?.participantLimitPerMap))
        ? Number(commercial.limits.participantLimitPerMap)
        : null,
      organizerLimit: Number.isFinite(Number(commercial?.limits?.organizerLimit))
        ? Number(commercial.limits.organizerLimit)
        : null,
      refreshBilling,
      openPlans,
      updateBilling,
    }),
    [
      aiFollowupRemaining,
      billing,
      canManageBilling,
      commercial,
      copiedFollowupRemaining,
      creationRemaining,
      error,
      isFree,
      isPaid,
      loading,
      openPlans,
      refreshBilling,
      updateBilling,
    ],
  );

  return <ConversionContext.Provider value={value}>{children}</ConversionContext.Provider>;
}

export function useConversion() {
  return useContext(ConversionContext);
}
